import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Send, Sparkles, Loader2, Users, Flame, Link2, ArrowLeft, Image as ImageIcon, Eye, EyeOff } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { MemberAvatar } from "@/components/MemberAvatar";
import { CoconBadge } from "@/components/CoconBadge";
import { CocoonMark } from "@/components/CocoonMark";

function MentorText({ content }) {
  const parts = content.split(/(\*\*[^*]+\*\*)/g);
  return (
    <p className="text-foreground/85 leading-[1.8] text-lg">
      {parts.map((p, i) =>
        p.startsWith("**") && p.endsWith("**") ? (
          <strong key={i} className="text-primary font-semibold">{p.slice(2, -2)}</strong>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </p>
  );
}

export default function DuoRoom() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const lang = (i18n.language || "fr").slice(0, 2);
  const storeKey = `coccon_duo_${id}`;

  const [room, setRoom] = useState(undefined); // undefined=loading, null=not found
  const [participant, setParticipant] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storeKey) || "null"); } catch { return null; }
  });
  const [nameInput, setNameInput] = useState("");
  const [hidePhoto, setHidePhoto] = useState(false);
  const [joining, setJoining] = useState(false);
  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [thinking, setThinking] = useState(false);
  const [thinkingBy, setThinkingBy] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);

  const sinceRef = useRef(null);
  const bottomRef = useRef(null);
  const joined = !!participant;

  useEffect(() => {
    api.get(`/duo/${id}`)
      .then(({ data }) => { setRoom(data); setParticipants(data.participants || []); })
      .catch(() => setRoom(null));
  }, [id]);

  const poll = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (sinceRef.current) params.set("since", sinceRef.current);
      if (participant?.pid) params.set("pid", participant.pid);
      const { data } = await api.get(`/duo/${id}/state?${params.toString()}`);
      setParticipants(data.participants || []);
      setThinking(!!data.thinking);
      setThinkingBy(data.thinking_by || null);
      if (data.messages && data.messages.length) {
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          const fresh = data.messages.filter((m) => !seen.has(m.id));
          return fresh.length ? [...prev, ...fresh] : prev;
        });
        const last = data.messages[data.messages.length - 1];
        if (last?.created_at) sinceRef.current = last.created_at;
        if (data.messages.some((m) => m.role === "user")) setPending([]);
      }
    } catch { /* ignore transient polling errors */ }
  }, [id, participant]);

  useEffect(() => {
    if (!joined) return;
    poll();
    const iv = setInterval(poll, 2500);
    return () => clearInterval(iv);
  }, [joined, poll]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, pending, thinking, sending]);

  const join = async () => {
    const name = nameInput.trim();
    if (!name) { toast.error(t("atelier.duoNameRequired")); return; }
    if (joining) return;
    setJoining(true);
    try {
      const { data } = await api.post(`/duo/${id}/join`, { name, hide_photo: hidePhoto });
      const p = { pid: data.participant_id, name: data.name };
      localStorage.setItem(storeKey, JSON.stringify(p));
      setParticipant(p);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("atelier.duoNotFound"));
    } finally {
      setJoining(false);
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    const tmpId = `tmp-${Date.now()}`;
    setPending((p) => [...p, { id: tmpId, role: "user", author: participant.name, content: text, mine: true }]);
    setSending(true);
    try {
      await api.post(`/duo/${id}/message`, { participant_id: participant.pid, content: text, lang });
      await poll();
    } catch (e) {
      setPending((p) => p.filter((x) => x.id !== tmpId));
      setInput(text);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("atelier.silent"));
    } finally {
      setSending(false);
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(window.location.href); } catch { /* ignore */ }
    toast.success(t("atelier.duoLinkCopied"));
  };

  // ---- loading / not found ----
  if (room === undefined) {
    return (
      <div className="min-h-screen starfield flex items-center justify-center">
        <p className="font-display italic text-2xl text-foreground/50 flex items-center gap-3">
          <Loader2 className="animate-spin" size={22} /> {t("atelier.duoLoading")}
        </p>
      </div>
    );
  }
  if (room === null) {
    return (
      <div className="min-h-screen starfield flex items-center justify-center px-6">
        <div className="glass rounded-3xl p-12 text-center max-w-md" data-testid="duo-notfound">
          <Flame className="text-foreground/30 mx-auto mb-6" size={34} />
          <p className="font-display italic text-2xl text-foreground/60 mb-8">{t("atelier.duoNotFound")}</p>
          <Link to="/" className="btn-frost-orange inline-flex items-center gap-2 px-7 py-3 rounded-full font-semibold text-sm">
            {t("atelier.duoBackHome")}
          </Link>
        </div>
      </div>
    );
  }

  const rendered = [...messages, ...pending];

  return (
    <div className="flex flex-col bg-background overflow-hidden starfield" style={{ height: "var(--vvh, 100dvh)" }}>
      {/* header */}
      <header className="glass z-30 flex-shrink-0">
        <div className="px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <button onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/atelier"))} data-testid="duoroom-back-btn"
              className="text-foreground/60 hover:text-primary transition-colors flex-shrink-0 -ml-1 p-1">
              <ArrowLeft size={20} />
            </button>
            <Link to="/" className="font-display text-lg sm:text-2xl tracking-tight whitespace-nowrap truncate" data-testid="duoroom-brand"><CocoonMark withMind /></Link>
          </div>
          <div className="flex items-center gap-3 sm:gap-5">
            {joined && (
              <button onClick={copyLink} data-testid="duoroom-share" className="hidden sm:inline-flex items-center gap-2 text-xs text-foreground/50 hover:text-primary transition-colors">
                <Link2 size={15} /> {t("atelier.duoShareLink")}
              </button>
            )}
            <LanguageSwitcher className="hidden sm:flex" />
          </div>
        </div>
        {joined && participants.length > 0 && (
          <div className="px-4 sm:px-6 pb-3 flex items-center gap-2 flex-wrap" data-testid="duoroom-participants">
            <Users size={14} className="text-primary/70" />
            <span className="text-xs text-foreground/40 uppercase tracking-wider">{t("atelier.duoAround")} :</span>
            {participants.map((p, i) => (
              <span key={i} className="text-xs pl-1 pr-2.5 py-0.5 rounded-full bg-primary/10 text-primary/90 flex items-center gap-1.5">
                <MemberAvatar uid={p.uid} avatar={p.avatar} name={p.name} cocon={p.cocon} size={22} />
                {p.name}{p.is_host ? " ★" : ""}{p.cocon && <CoconBadge />}
              </span>
            ))}
          </div>
        )}
      </header>

      {!joined ? (
        <div className="flex-1 flex items-center justify-center px-6">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-3xl p-10 frame-pulse w-full max-w-md text-center" data-testid="duo-join-card">
            <div className="w-16 h-16 rounded-full bg-primary/15 flex items-center justify-center mx-auto mb-6">
              <Flame className="text-primary" size={26} />
            </div>
            <div className="uppercase tracking-[0.22em] text-xs text-primary/80 mb-2">{t("atelier.duoRoomEyebrow")}</div>
            <h1 className="font-display text-3xl text-gradient-orange mb-3">{t("atelier.duoJoinTitle")}</h1>
            {room.topic && <p className="font-display italic text-foreground/60 mb-4">« {room.topic} »</p>}
            <p className="text-foreground/55 mb-6">{t("atelier.duoJoinSub")}</p>
            <input
              data-testid="duo-name-input"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && join()}
              placeholder={t("atelier.duoNamePlaceholder")}
              maxLength={40}
              className="w-full bg-black/30 border border-white/15 rounded-full px-5 py-3 text-center outline-none text-foreground placeholder:text-foreground/30 focus:border-primary/50 transition-colors mb-5"
            />
            <button
              data-testid="duo-join-btn"
              onClick={join}
              disabled={joining}
              className="btn-frost-orange w-full inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-semibold text-base disabled:opacity-50"
            >
              {joining ? <Loader2 size={18} className="animate-spin" /> : <Users size={18} />}
              {joining ? t("atelier.duoJoining") : t("atelier.duoJoinBtn")}
            </button>
            <p className="text-[11px] text-foreground/40 leading-relaxed mt-4 flex items-center justify-center gap-1.5" data-testid="duo-photo-note">
              <ImageIcon size={12} className="flex-shrink-0" /> {t("atelier.duoPhotoNote")}
            </p>
          </motion.div>
        </div>
      ) : (
        <>
          <div className="flex-1 overflow-y-auto scroll-thin">
            <div className="max-w-3xl mx-auto px-6 py-10 space-y-8">
              {rendered.length === 0 && !thinking && (
                <p className="font-display italic text-foreground/40 text-xl text-center py-10">{t("atelier.duoEmpty")}</p>
              )}
              {rendered.map((m) => (
                <motion.div key={m.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} data-testid={`duo-msg-${m.role}`}>
                  {m.role === "assistant" ? (
                    <div className="flex gap-4 pl-0 md:pl-2">
                      <Sparkles className="text-primary mt-1.5 flex-shrink-0 pulse-star" size={20} />
                      <div className="border-l border-primary/20 pl-5 flex-1">
                        <MentorText content={m.content} />
                      </div>
                    </div>
                  ) : (
                    <div className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                      <div className={`flex items-start gap-2.5 max-w-xl ${m.mine ? "flex-row-reverse" : "flex-row"}`}>
                        <MemberAvatar uid={m.uid} avatar={m.avatar} name={m.author} cocon={m.cocon} size={30} />
                        <div className={m.mine ? "text-right" : "text-left"}>
                          <div className={`text-xs uppercase tracking-wider mb-1 inline-flex items-center gap-1.5 ${m.mine ? "flex-row-reverse text-primary/80" : "text-foreground/40"}`}>{m.author}{m.cocon && <CoconBadge />}</div>
                          <p className="font-display italic text-xl text-foreground/75 leading-relaxed">« {m.content} »</p>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
              <AnimatePresence>
                {(thinking || sending) && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex gap-4 pl-0 md:pl-2" data-testid="duo-thinking">
                    <Sparkles className="text-primary mt-1.5 pulse-star" size={20} />
                    <p className="font-display italic text-foreground/40 text-lg">{t("atelier.duoThinking")}</p>
                  </motion.div>
                )}
              </AnimatePresence>
              <div ref={bottomRef} />
            </div>
          </div>

          <div className="flex-shrink-0 border-t border-white/10 glass">
            <div className="w-full px-3 sm:px-6 py-3 flex items-end gap-2 sm:gap-3">
              <textarea
                data-testid="duo-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                rows={1}
                placeholder={t("atelier.duoPlaceholder")}
                className="flex-1 bg-transparent resize-none outline-none text-foreground placeholder:text-foreground/30 text-lg py-3 max-h-40 scroll-thin"
              />
              <button
                data-testid="duo-send-btn"
                onClick={send}
                disabled={sending || !input.trim()}
                className="flex-shrink-0 w-11 h-11 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors disabled:opacity-30"
              >
                {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
